<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import WootModalHeader from 'dashboard/components/ModalHeader.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  message: {
    type: Object,
    required: true,
    default: () => ({ content: '' }),
  },
});

const emit = defineEmits(['close', 'save']);

const editedContent = ref('');
const textareaRef = ref(null);

const sanitizeContent = content =>
  (content ?? '')
    .replace(/\\\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

const isModified = computed(() => {
  return (
    editedContent.value.trim() !== sanitizeContent(props.message.content) &&
    editedContent.value.trim() !== ''
  );
});

const closeModal = () => {
  emit('close');
};

const handleSave = () => {
  if (isModified.value) {
    emit('save', editedContent.value.trim());
  }
};

const moveCursorToEnd = event => {
  const target = event.target;
  target.setSelectionRange(target.value.length, target.value.length);
};

const adjustTextareaHeight = () => {
  const textarea = textareaRef.value;
  if (textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }
};

watch(
  () => props.message.content,
  newContent => {
    editedContent.value = sanitizeContent(newContent);
  },
  { immediate: true }
);

watch(editedContent, () => {
  nextTick(adjustTextareaHeight);
});

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      nextTick(() => {
        textareaRef.value?.focus();
        adjustTextareaHeight();
      });
    }
  }
);
</script>

<template>
  <Modal :show="isOpen" :on-close="closeModal" size="medium">
    <WootModalHeader
      :header-title="$t('CONVERSATION.EDIT_MESSAGE.TITLE')"
      :header-content="$t('CONVERSATION.EDIT_MESSAGE.SUB_TITLE')"
    />

    <div class="px-8 pt-6 pb-0">
      <div
        class="relative bg-slate-50 dark:bg-slate-800/50 rounded-lg border-slate-200 dark:border-slate-700 transition-colors duration-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20"
      >
        <textarea
          ref="textareaRef"
          v-model="editedContent"
          class="w-full min-h-[120px] p-3 bg-transparent text-slate-800 dark:text-slate-100 resize-none focus:outline-none overflow-y-hidden placeholder:text-slate-400"
          rows="1"
          :placeholder="$t('CONVERSATION.EDIT_MESSAGE.TEXTAREA_PLACEHOLDER')"
          @focus="moveCursorToEnd"
        />
      </div>
    </div>

    <div class="flex justify-end items-center gap-2 w-full px-8 py-6">
      <WootButton variant="clear" @click="closeModal">
        {{ $t('CONVERSATION.EDIT_MESSAGE.BUTTONS.CANCEL') }}
      </WootButton>
      <WootButton
        :is-disabled="!isModified"
        icon="checkmark"
        @click="handleSave"
      >
        {{ $t('CONVERSATION.EDIT_MESSAGE.BUTTONS.SAVE') }}
      </WootButton>
    </div>
  </Modal>
</template>
